'use client'

import { checkboxClasses } from '@mui/material/Checkbox'
import { stepIconClasses } from '@mui/material/StepIcon'
import { stepLabelClasses } from '@mui/material/StepLabel'
import { alpha, createTheme } from '@mui/material/styles'

import { colors } from '@/themes/commerce/colors'
import { fontNames } from '@/themes/commerce/fonts'

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1440,
    },
  },
  customDimensions: {
    bannerMinHeight: 200,
    drawerWidth: 365,
    facetHeight: 350,
    heroBannerMinHeight: 440,
    heroBannerTextSpace: 32,
    megaMenuHeight: 492,
    miniBarIconSize: { sx: 24, md: 32 },
    productCardImageHeight: 205,
    productListImageHeight: 88,
    productDetailsImageHeight: 350,
    productCardWidth: 350,
    searchHeaderOffset: 102,
    welcomeDialog: 576,
    welcomeDialogMobile: 400,
  },
  palette: {
    primary: {
      main: colors.brand[2],
      light: colors.brand[3],
    },
    secondary: {
      main: colors.brand[1],
    },
    success: {
      dark: colors.feedback.success,
      light: colors.feedback.successBackground,
      main: colors.feedback.success,
    },
    warning: {
      dark: colors.feedback.warning,
      light: colors.feedback.warningBackground,
      main: colors.feedback.warning,
    },
    error: {
      dark: colors.feedback.error,
      light: colors.feedback.errorBackground,
      main: colors.feedback.error,
    },
    chip: {
      main: colors.background.component.default,
      contrastText: colors.background.component.neutral,
    },

    text: {
      disabled: colors.text.disabled,
      light: colors.text.inverse,
      primary: colors.text.default,
      promo: colors.text.link,
      secondary: colors.brand[1],
      placeholder: colors.text.placeholder,
      labelText: colors.text.labelText,
      titleSection: colors.text.titleSection,
      campaignPrice: colors.text.campaignPrice,
      appropriationPrice: colors.text.appropriationLabelText,
    },
    background: {
      body: colors.background.page.light,
      dark: colors.background.section.dark,
      default: colors.background.component.neutral,
      light: colors.background.page.light,
      pale: colors.background.page.pale,
      component: colors.background.component.default,
      paper: colors.background.page.light,
      ribbon: colors.background.component.cta,
      info: colors.background.component.subtle,
      carouselButton: colors.background.component.carouselButton,
      carouselButtonHover: colors.background.component.carouselButtonHover,
      campaignLabel: colors.background.component.campaignLabel,
    },
    border: {
      default: colors.border.default,
      input: colors.border.input,
      button: colors.border.default,
      inputHover: colors.border.inputHover,
      labelTag: colors.border.labelTag,
      campaignLabelTag: colors.border.campaignLabel,
      appropriationLabelTag: colors.border.appropriationLabel,
    },
    divider: colors.border.default,
  },
  typography: {
    hero: {
      fontFamily: fontNames.nunito,
      fontSize: '40px',
      fontWeight: 700,
      lineHeight: '48px',
    },
    banner1: {
      fontFamily: fontNames.nunito,
      fontSize: '3rem',
      fontWeight: 900,
      letterSpacing: '1.92px',
      lineHeight: '4rem',
    },
    banner2: {
      fontFamily: fontNames.nunito,
      fontSize: '2.125rem',
      fontWeight: 700,
      letterSpacing: '1.36px',
      lineHeight: '2rem',
      padding: 0,
    },
    h1: {
      fontFamily: fontNames.nunito,
      fontSize: '2.5rem',
      fontWeight: 700,
      letterSpacing: 'normal',
      lineHeight: '3rem',
    },
    h2: {
      fontFamily: fontNames.nunito,
      fontSize: '2rem',
      fontWeight: 700,
      letterSpacing: 'normal',
      lineHeight: '2.5rem',
    },
    h3: {
      fontFamily: fontNames.nunito,
      fontSize: '1.5rem',
      lineHeight: '2rem',
      fontWeight: 700,
      letterSpacing: 'normal',
    },
    h4: {
      fontFamily: fontNames.nunito,
      fontSize: '1.25rem',
      fontWeight: 700,
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1.5rem',
      letterSpacing: 'normal',
    },
    h5: {
      fontFamily: fontNames.nunito,
      fontWeight: 700,
      fontSize: '1rem',
      lineHeight: '1.25rem',
    },
    h6: {
      fontFamily: fontNames.nunito,
      fontWeight: 700,
      fontSize: '0.875rem',
      lineHeight: '1.5rem',
    },
    h7: {
      fontFamily: fontNames.nunito,
      fontSize: '0.75rem',
      lineHeight: '1.5rem',
    },
    body1: {
      fontFamily: fontNames.nunito,
      fontSize: '1rem',
      lineHeight: '1.5rem',
      letterSpacing: '0.4px',
    },
    body2: {
      fontFamily: fontNames.nunito,
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
    },
    button: {
      fontFamily: fontNames.nunito,
      fontSize: '0.875rem',
      lineHeight: '1.5rem',
      fontWeight: 700,
      letterSpacing: '1.25px',
    },
    tab: {
      fontFamily: fontNames.nunito,
      fontSize: '0.875rem',
      lineHeight: '1.5rem',
      fontWeight: 700,
      letterSpacing: '0.35px',
    },
    caption: {
      fontFamily: fontNames.nunito,
      fontSize: '0.875rem',
      letterSpacing: '0.35px',
      lineHeight: '1.5rem',
    },
    ribbon: {
      fontFamily: fontNames.nunito,
      fontWeight: '900',
      letterSpacing: '0.56px',
      fontSize: '14px',
      lineHeight: '20px',
    },
    description: {
      fontFamily: fontNames.nunito,
      fontSize: '14px',
      lineHeight: '20px',
      color: colors.text.placeholder,
    },
    productName: {
      fontFamily: fontNames.nunito,
      fontWeight: 700,
      fontSize: '16px',
      lineHeight: '20px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      WebkitLineClamp: '2',
      WebkitBoxOrient: 'vertical',
      textTransform: 'capitalize',
    },
    productNameLong: {
      fontFamily: fontNames.nunito,
      fontWeight: 700,
      fontSize: '16px',
      lineHeight: '20px',
      textTransform: 'capitalize',
    },
    pageHeading: {
      fontSize: '40px',
      fontWeight: 700,
      lineHeight: '2rem',
    },
    pageHeading1: {
      fontSize: '32px',
      fontWeight: 700,
      lineHeight: '40px',
      color: colors.text.heading,
    },
    pageHeading3: {
      fontSize: '24px',
      fontWeight: 700,
      lineHeight: '32px',
      color: colors.text.heading,
    },
    pageHeading4: {
      fontSize: '20px',
      fontWeight: 700,
      lineHeight: '24px',
      color: colors.text.heading,
    },
    pageTitle: {
      fontSize: '20px',
      fontWeight: 700,
      lineHeight: '24px',
      color: colors.text.heading,
    },
    fontFamily: fontNames.nunito,
  },
})

export const commerceTheme = createTheme(
  {
    components: {
      MuiTypography: {
        defaultProps: {
          component: 'p',
        },
        variants: [
          {
            props: { variant: 'pageHeading' },
            style: {
              color: colors.text.heading,
            },
          },
        ],
      },
      MuiStepConnector: {
        styleOverrides: {
          alternativeLabel: () => {
            const stepIconSize = 32

            return {
              top: stepIconSize / 2,
              left: `calc(-50% + ${stepIconSize / 2}px)`,
              right: `calc(50% + ${stepIconSize / 2}px)`,
            }
          },
          line: ({ ownerState, theme }) => ({
            borderColor: theme.palette.grey[700],
            ...((ownerState.completed || ownerState.active) && {
              borderColor: theme.palette.secondary.main,
            }),
          }),
        },
      },
      MuiStepButton: {
        defaultProps: {
          disableRipple: true,
        },
      },
      MuiStepLabel: {
        styleOverrides: {
          label: {
            color: theme.palette.text.primary,
            fontWeight: theme.typography.fontWeightBold,
            [`&.${stepLabelClasses.completed}, &.${stepLabelClasses.active}`]: {
              color: theme.palette.text.primary,
              fontWeight: theme.typography.fontWeightBold,
            },
          },
        },
      },
      MuiStepIcon: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.grey[300],
            fontWeight: theme.typography.fontWeightBold,
            width: 32,
            height: 32,
            [`&.${stepIconClasses.active}, &.${stepIconClasses.completed}`]: {
              color: theme.palette.secondary.main,
            },
          }),
          text: ({ ownerState, theme }) => ({
            fill: theme.palette.text.primary,
            ...(ownerState.active && {
              fill: theme.palette.text.light,
            }),
          }),
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            '&.partiallyVisible': {
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                width: '100%',
                height: theme.spacing(4),
                display: 'block',
                background: `linear-gradient(to top, ${theme.palette.background.default} 25%, ${alpha(
                  theme.palette.background.default,
                  0
                )} 100%)`,
              },
              '&.Mui-expanded::after': {
                height: 0,
              },
              '& .MuiCollapse-root': {
                minHeight: `${theme.spacing(11)}!important`,
                height: theme.spacing(11),
                '&.MuiCollapse-hidden .MuiAccordionDetails-root': {
                  display: '-webkit-box',
                  WebkitLineClamp: '3',
                  WebkitBoxOrient: 'vertical',
                },
                '& .MuiCollapse-wrapperInner': {
                  visibility: 'visible',
                },
              },
            },
            '&.Mui-disabled .MuiSvgIcon-root': {
              color: theme.palette.text.primary,
            },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          content: theme.typography.button,
        },
      },
      MuiCssBaseline: {
        styleOverrides: `
          @font-face {
            font-family: 'Nunito';
            font-style: normal;
            font-display: swap;
            font-weight: 400;
            src: url('/fonts/Nunito-Regular.woff') format('woff');
            unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA,
              U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215,
              U+FEFF;
          }
          @font-face {
            font-family: 'Nunito';
            font-style: normal;
            font-display: swap;
            font-weight: 700;
            src: url('/fonts/Nunito-Bold.woff') format('woff');
            unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA,
              U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215,
              U+FEFF;
          }
          body: {
            fontSize: '16px',
            letterSpacing: '0.4px',
            background: theme.palette.background.body,
          }`,
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            '&.MuiFormControl-fullWidth': {
              width: '100%',
              maxWidth: '100%',
            },
            '.MuiFormLabel-filled': {
              color: theme.palette.text.placeholder,
            },
            '.MuiFormLabel-filled ~ .MuiFilledInput-root::after': {
              transform: 'scaleX(1)',
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            display: 'flex',
            gap: theme.spacing(1),
            color: theme.palette.secondary.dark,
          },
        },
      },
      MuiIconButton: {
        defaultProps: {
          color: 'inherit',
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            '&.MuiFormLabel-filled': {
              color: theme.palette.text.primary,
            },
            '&.Mui-focused': {
              color: theme.palette.text.primary,
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            fontSize: '1rem',
            '&.MuiAlert-root.MuiAlert-outlined': {
              backgroundColor: theme.palette.background.default,
            },
          },
        },
      },
      MuiAlertTitle: {
        styleOverrides: {
          root: {
            fontWeight: theme.typography.fontWeightBold,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderColor: theme.palette.border.input,
            backgroundColor: theme.palette.background.default,
            borderRadius: '2px',
            input: {
              padding: '12px 16px',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline, :hover .MuiOutlinedInput-notchedOutline': {
              borderWidth: '1px',
              borderColor: theme.palette.border.inputHover,
            },
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            backgroundColor: theme.palette.background.default,
            '&:active': {
              backgroundColor: theme.palette.background.default,
            },
            '&:hover:not(.Mui-disabled)': {
              backgroundColor: theme.palette.background.default,
              '&:before': {
                borderBottomWidth: 2,
                borderBottomColor: theme.palette.border.input,
              },
            },
            '&:focused': {
              backgroundColor: theme.palette.background.default,
            },
            '&.Mui-focused': {
              backgroundColor: theme.palette.background.default,
            },
            '&:before': {
              borderBottomWidth: 2,
              borderBottomColor: theme.palette.border.input,
            },
          },
          input: {
            '&:focus': {
              backgroundColor: theme.palette.background.default,
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            '&.MuiInputLabel-filled': {
              transform: 'translate(12px, 0) scale(0.75)',
            },
            color: theme.palette.text.disabled,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: theme.palette.background.body,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            backgroundColor: theme.palette.background.dark,
            color: theme.palette.background.default,
            fontSize: '1rem',
            marginBottom: theme.spacing(2),
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            backgroundColor: theme.palette.background.body,
          },
        },
      },
      MuiDialogContentText: {
        styleOverrides: {
          root: {
            color: theme.palette.text.primary,
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: theme.spacing(2),
            paddingTop: theme.spacing(),
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            fontSize: '1rem',
            textTransform: 'none',
            letterSpacing: 0,
            padding: '10px',
            '&.MuiButton-containedSuccess': {
              color: theme.palette.background.default,
            },
          },
          outlined: {
            borderRadius: '4px',
            borderWidth: '2px',
            borderColor: theme.palette.secondary.main,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.secondary.main,
            '&:hover': {
              borderWidth: '2px',
              borderColor: 'transparent',
              backgroundColor: theme.palette.secondary.main,
              color: theme.palette.text.light,
            },
            '&.Mui-disabled': {
              borderWidth: '2px',
            },
          },
          contained: {
            backgroundColor: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: theme.palette.secondary.main,
            },
          },
        },
        defaultProps: {
          disableElevation: true,
        },
        variants: [
          {
            props: { variant: 'link' },
            style: {
              textTransform: 'none',
              padding: '0',
              letterSpacing: 'normal',
              minWidth: '0',
              color: 'inherit',
              fontWeight: 'normal',
              '&:hover': { textDecoration: 'underline', backgroundColor: 'transparent' },
            },
          },
          {
            props: { variant: 'linkBold' },
            style: {
              textTransform: 'none',
              padding: '0',
              letterSpacing: 'normal',
              minWidth: '0',
              color: 'inherit',
              fontWeight: 'bold',
              '&:hover': { backgroundColor: 'transparent' },
              '&, & .MuiSvgIcon-root': {
                color: 'red',
              },
            },
          },
          {
            props: { variant: 'outlinedFocused' },
            style: {
              borderWidth: '2px',
              borderStyle: 'solid',
              borderColor: 'transparent',
              backgroundColor: theme.palette.secondary.main,
              color: theme.palette.text.light,
              borderRadius: '4px',
              '&:hover': {
                borderWidth: '2px',
                borderColor: theme.palette.secondary.main,
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.secondary.main,
              },
              '&.Mui-disabled': {
                borderWidth: '2px',
              },
            },
          },
        ],
      },
      MuiButtonGroup: {
        variants: [
          {
            props: { variant: 'base' },
            style: {},
          },
        ],
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            [`&.${checkboxClasses.checked}`]: {
              color: theme.palette.text.secondary,
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            '.Mui-checked.Mui-checked+.MuiSwitch-track.MuiSwitch-track': {
              opacity: 0.1,
            },
          },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          actions: {
            color: theme.palette.text.primary,
          },
          selectIcon: {
            color: theme.palette.text.primary,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          head: {
            th: {
              verticalAlign: 'text-top',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontSize: '1rem',
            borderBottomColor: theme.palette.background.pale,
            // borderBottom: 'none',
          },
        },
        variants: [
          {
            props: { variant: 'header' },
            style: {
              fontFamily: theme.typography.h5.fontFamily,
              fontWeight: theme.typography.h5.fontWeight,
              fontSize: theme.typography.h5.fontSize,
              lineHeight: theme.typography.h5.lineHeight,
              color: theme.palette.secondary.main,
              backgroundColor: theme.palette.primary.light,
            },
          },
          {
            props: { variant: 'header2' },
            style: {
              fontFamily: theme.typography.h5.fontFamily,
              fontWeight: theme.typography.h5.fontWeight,
              fontSize: theme.typography.h5.fontSize,
              lineHeight: theme.typography.h5.lineHeight,
              color: theme.palette.secondary.main,
              borderBottomColor: theme.palette.background.pale,
            },
          },
          {
            props: { variant: 'small' },
            style: {
              fontFamily: theme.typography.body2.fontFamily,
              fontWeight: theme.typography.body2.fontWeight,
              fontSize: theme.typography.body2.fontSize,
              lineHeight: theme.typography.body2.lineHeight,
              borderBottomColor: theme.palette.background.pale,
            },
          },
          {
            props: { variant: 'noBorder' },
            style: {
              borderBottom: 'none',
            },
          },
        ],
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: '1px',
              left: 0,
              right: 0,
              borderBottom: `1px solid ${theme.palette.border.input}`,
              zIndex: '-1',
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            color: theme.palette.text.light,
            '&.Mui-selected': {
              color: colors.text.link,
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          icon: {
            color: colors.text.link,
          },
          select: {
            padding: '12px 16px',
          },
        },
      },
      MuiList: {
        styleOverrides: {
          root: {
            padding: 0,
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            span: { color: colors.text.default },
            '&:hover': {
              '.MuiListItemButton-root': {
                backgroundColor: colors.background.component.subtle,
                span: { color: colors.text.link },
              },
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            height: theme.spacing(6),
            '&.Mui-selected,&.Mui-selected:focus': {
              backgroundColor: colors.background.component.ctaInverseHover,
            },
          },
        },
      },
      MuiCard: {
        defaultProps: {
          elevation: 0,
          square: true,
        },
      },
      MuiCardHeader: {
        defaultProps: {
          titleTypographyProps: {
            variant: 'body2',
          },
        },
      },
      MuiContainer: {
        defaultProps: {
          fixed: true,
          maxWidth: 'xl',
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            '&.MuiRadio-colorError': {
              color: theme.palette.error.main,
            },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            '& label': {
              fontSize: 14,
            },
          },
          input: {
            fontSize: 14,
          },
          listbox: {
            fontSize: 14,
          },
        },
      },
      MuiIcon: {
        defaultProps: {
          baseClassName: 'material-icons-outlined',
        },
      },
      MuiTextField: {
        defaultProps: {
          size: 'small',
        },
        styleOverrides: {
          // Hide arrows on number input
          root: {
            '& input[type="number"]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
              WebkitAppearance: 'none',
              appearance: 'none',

              margin: 0,
            },
            'input[type=number]': {
              MozAppearance: 'textfield',
            },
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            marginTop: theme.spacing(-0.5),
          },
        },
      },
      MuiSkeleton: {
        defaultProps: {
          animation: 'wave',
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: theme.palette.secondary.main,
            textDecorationColor: `${theme.palette.secondary.main}66`,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderBottomWidth: 2,
            width: '100%',
            paddingBlock: '12px',
            paddingInline: '22px',
            justifyContent: 'space-between',
            textDecoration: 'none',
            '&.Mui-selected, &.Mui-focusVisible, &.Mui-selected:hover, &.Mui-focusVisible:hover': {
              backgroundColor: colors.background.component.subtle,
            },
          },
        },
      },
    },
  },
  theme
)
